import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Eye, Loader2, FileText, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductSeoEditorPage() {
  const { id } = useParams();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/products/${id}/seo`, { headers: authHeaders() });
        setProduct(res.data);
        setTitle(res.data.title || '');
        setDescription(res.data.description || '');
        setSeoDescription(res.data.seo_description || '');
      } catch { toast.error('Nie znaleziono produktu'); }
      setLoading(false);
    };
    fetch();
  }, [id, authHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${id}/seo`, {
        title, description, seo_description: seoDescription
      }, { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
      toast.success('Zapisano!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd zapisu'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  if (!product) return <div className="text-center py-20 text-muted-foreground">Produkt nie znaleziony</div>;

  const img = product.image_local_path ? `${BACKEND}${product.image_local_path}` : product.image_url;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="seo-editor-page">
      <button onClick={() => navigate('/admin')} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Panel admina
      </button>

      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-xl font-extrabold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" /> Edycja SEO produktu
        </h1>
        <div className="flex gap-2">
          <Button onClick={() => setPreviewMode(!previewMode)} className="bg-secondary text-secondary-foreground rounded-none text-xs" data-testid="toggle-preview">
            <Eye className="w-3.5 h-3.5 mr-1" /> {previewMode ? 'Edycja' : 'Podgląd'}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground rounded-none font-heading uppercase text-xs" data-testid="save-seo-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Zapisz
          </Button>
          <a href={`/produkt/${id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-3 py-2 text-xs rounded-none hover:bg-secondary/80">
            <ExternalLink className="w-3.5 h-3.5" /> Podgląd strony
          </a>
        </div>
      </div>

      {/* Product info card */}
      <div className="bg-card border border-border/40 p-4 mb-4 flex items-center gap-4">
        {img && <img src={img} alt="" className="w-16 h-16 object-cover bg-secondary flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium truncate">{product.title}</p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            {product.deal_price && <span className="text-primary font-bold">{product.deal_price} zł</span>}
            <span>{product.category}</span>
            {product.seo_generated_at && <span>SEO: {new Date(product.seo_generated_at).toLocaleDateString('pl-PL')}</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor */}
        <div className="space-y-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Tytuł produktu</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} className="bg-input border-transparent focus:border-primary rounded-none" data-testid="edit-title" />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Opis bazowy</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="bg-input border-transparent focus:border-primary rounded-none resize-none text-sm" data-testid="edit-description" />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Opis SEO (HTML)</Label>
            <Textarea
              value={seoDescription}
              onChange={e => setSeoDescription(e.target.value)}
              rows={20}
              className="bg-input border-transparent focus:border-primary rounded-none resize-y font-mono text-xs leading-relaxed"
              data-testid="edit-seo-description"
            />
          </div>
        </div>

        {/* Preview */}
        <div>
          <div className="bg-card border border-border/40 p-4 sticky top-20">
            <h3 className="font-heading text-sm font-bold text-white uppercase tracking-wider mb-3 border-b border-border/30 pb-2">Podgląd SEO</h3>
            <div className="seo-content max-h-[600px] overflow-y-auto pr-2">
              {seoDescription ? (
                <div dangerouslySetInnerHTML={{ __html: seoDescription }} />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">Brak opisu SEO. Wpisz HTML lub wygeneruj automatycznie.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
